import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { ArrowLeft, Plus, Star, Trash2, Pencil } from "lucide-react";

function preview(prefix, sep, width, value) {
  const sfx = String(value).padStart(Math.max(1, width || 1), "0");
  return prefix ? `${prefix}${sep || ""}${sfx}` : sfx;
}

export default function NumberingSeriesPage() {
  const { projectId } = useParams();
  const { user } = useAuth();
  const canManage = ["admin", "qa_manager"].includes(user?.role);
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState([]);
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(null); // {mode: 'create'|'edit', series?}

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [proj, series] = await Promise.all([
        api.get(`/projects/${projectId}`),
        api.get(`/projects/${projectId}/numbering-series`),
      ]);
      setProject(proj.data);
      setItems(series.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Unable to load series");
    } finally {
      setLoading(false);
    }
  }, [projectId]);
  useEffect(() => { reload(); }, [reload]);

  const setDefault = async (s) => {
    try {
      await api.put(`/numbering-series/${s.id}`, { is_default: true });
      toast.success(`${s.name} set as default`);
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const toggleActive = async (s) => {
    try {
      await api.put(`/numbering-series/${s.id}`, { active: !s.active });
      toast.success(s.active ? "Series deactivated" : "Series activated");
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const del = async (s) => {
    if (!window.confirm(`Delete series "${s.name}"? If it has issued any lot numbers, it will be deactivated instead of hard-deleted.`)) return;
    try {
      const { data } = await api.delete(`/numbering-series/${s.id}`);
      toast.success(data.deleted ? "Series deleted" : "Series deactivated (in use)");
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="mx-auto max-w-5xl p-4 md:p-8" data-testid="numbering-series-page">
      <Link to="/projects" className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-800" data-testid="numbering-back">
        <ArrowLeft className="size-3" /> Projects
      </Link>
      <div className="mt-2 flex items-start justify-between gap-3">
        <div>
          <div className="overline mb-1">Lot Numbering</div>
          <h1 className="font-heading text-3xl font-black tracking-tighter text-slate-900 sm:text-4xl">
            {project?.name || "Project"} — Numbering Series
          </h1>
          <p className="mt-1 text-sm text-slate-600">Configure per-project numbering series. Lot numbers are issued atomically on creation.</p>
        </div>
        {canManage && (
          <Button onClick={() => setDialog({ mode: "create" })} className="bg-teal-700 hover:bg-teal-800" data-testid="add-series-btn">
            <Plus className="mr-1 size-4" /> Add Series
          </Button>
        )}
      </div>

      <Card className="mt-4 overflow-hidden border-slate-200">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs text-slate-600">
            <tr>
              <th className="px-3 py-2 text-left">Name</th>
              <th className="px-3 py-2 text-left">Format</th>
              <th className="px-3 py-2 text-left">Preview</th>
              <th className="px-3 py-2 text-left">Issued</th>
              <th className="px-3 py-2 text-left">Default</th>
              <th className="px-3 py-2 text-left">Active</th>
              <th className="px-3 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={7} className="p-4 text-sm text-slate-500">Loading…</td></tr>}
            {!loading && items.length === 0 && <tr><td colSpan={7} className="p-4 text-sm text-slate-500" data-testid="numbering-empty">No numbering series configured. Add one to enable auto-numbering for new lots.</td></tr>}
            {items.map(s => (
              <tr key={s.id} className="border-t border-slate-200 hover:bg-slate-50" data-testid={`series-row-${s.id}`}>
                <td className="px-3 py-2 font-semibold text-slate-900">{s.name}</td>
                <td className="px-3 py-2 font-mono text-xs text-slate-700">{s.prefix || "(none)"}{s.separator || ""}{"0".repeat(s.suffix_width)}</td>
                <td className="px-3 py-2 font-mono text-teal-800" data-testid={`series-preview-${s.id}`}>{preview(s.prefix, s.separator, s.suffix_width, s.next_value)}</td>
                <td className="px-3 py-2 text-slate-600">{s.issued_count || 0}</td>
                <td className="px-3 py-2">
                  {s.is_default ? <span className="inline-flex items-center gap-1 rounded bg-amber-100 px-2 py-0.5 text-xs text-amber-800"><Star className="size-3" /> Default</span> : (
                    canManage && s.active && <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setDefault(s)} data-testid={`series-set-default-${s.id}`}>Set default</Button>
                  )}
                </td>
                <td className="px-3 py-2">
                  {canManage ? (
                    <button onClick={() => toggleActive(s)} className={`rounded px-2 py-0.5 text-xs ${s.active ? "bg-emerald-100 text-emerald-800" : "bg-slate-200 text-slate-600"}`} data-testid={`series-toggle-active-${s.id}`}>
                      {s.active ? "Active" : "Inactive"}
                    </button>
                  ) : (
                    <span className="text-xs text-slate-500">{s.active ? "Active" : "Inactive"}</span>
                  )}
                </td>
                <td className="px-3 py-2 text-right">
                  {canManage && (
                    <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setDialog({ mode: "edit", series: s })} data-testid={`series-edit-${s.id}`}>
                      <Pencil className="mr-1 size-3" /> Edit
                    </Button>
                  )}
                  {isAdmin && (
                    <Button size="sm" variant="outline" className="ml-1 h-7 text-xs text-rose-700 hover:bg-rose-50" onClick={() => del(s)} data-testid={`series-delete-${s.id}`}>
                      <Trash2 className="size-3" />
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      {dialog && (
        <SeriesDialog
          projectId={projectId}
          mode={dialog.mode}
          series={dialog.series}
          onClose={() => setDialog(null)}
          onSaved={() => { setDialog(null); reload(); }}
        />
      )}
    </div>
  );
}

function SeriesDialog({ projectId, mode, series, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: series?.name || "",
    prefix: series?.prefix || "",
    suffix_width: series?.suffix_width || 4,
    separator: series?.separator ?? "-",
    start_value: series?.start_value || 1,
    is_default: series?.is_default || false,
    active: series?.active !== false,
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      if (mode === "create") {
        await api.post(`/projects/${projectId}/numbering-series`, form);
        toast.success("Series created");
      } else {
        const patch = {};
        ["name", "prefix", "suffix_width", "separator", "is_default", "active"].forEach(k => {
          if (form[k] !== (series[k] ?? "")) patch[k] = form[k];
        });
        await api.put(`/numbering-series/${series.id}`, patch);
        toast.success("Series updated");
      }
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="series-dialog">
        <DialogHeader>
          <DialogTitle>{mode === "create" ? "Add Numbering Series" : `Edit — ${series.name}`}</DialogTitle>
          <DialogDescription>Configure the prefix, separator and suffix width. Numbers are issued atomically when a new lot is created.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 gap-3">
          <div>
            <Label>Name *</Label>
            <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g. Drainage" data-testid="series-name-input" />
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <Label>Prefix</Label>
              <Input value={form.prefix} onChange={e => setForm({ ...form, prefix: e.target.value })} placeholder="PIT" data-testid="series-prefix-input" />
            </div>
            <div>
              <Label>Separator</Label>
              <Input value={form.separator} onChange={e => setForm({ ...form, separator: e.target.value })} placeholder="-" data-testid="series-separator-input" />
            </div>
            <div>
              <Label>Suffix width</Label>
              <Input type="number" min={1} max={8} value={form.suffix_width} onChange={e => setForm({ ...form, suffix_width: parseInt(e.target.value || "0") })} data-testid="series-width-input" />
            </div>
          </div>
          {mode === "create" && (
            <div>
              <Label>Start value</Label>
              <Input type="number" min={1} value={form.start_value} onChange={e => setForm({ ...form, start_value: parseInt(e.target.value || "0") })} data-testid="series-start-input" />
            </div>
          )}
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.is_default} onChange={e => setForm({ ...form, is_default: e.target.checked })} data-testid="series-default-checkbox" />
            <span>Use as project default</span>
          </label>
          <div className="rounded border border-slate-200 bg-slate-50 p-2 font-mono text-sm text-slate-700" data-testid="series-preview">
            Preview: <span className="text-teal-700">{preview(form.prefix, form.separator, form.suffix_width, mode === "create" ? form.start_value : (series?.next_value || 1))}</span>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="series-save-btn">{mode === "create" ? "Create" : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
